import type { Language } from '../i18n/Language';

/** Display only — the edge where an integer or an ISO string becomes text (Conventions/06 §5). */
const LOCALES: Record<Language, string> = { sk: 'sk-SK', en: 'en-GB' };

const NONE = '—';

/** Minor units to a currency string. `null` is "no price", not zero. */
export const formatMoney = (minor: number | null, currency: string, language: Language): string =>
  minor === null
    ? NONE
    : new Intl.NumberFormat(LOCALES[language], { style: 'currency', currency }).format(minor / 100);

export const formatNumber = (value: number, language: Language): string =>
  new Intl.NumberFormat(LOCALES[language]).format(value);

/** A rate in basis points — 2300 — as a percentage: "23 %" in Slovak, "23%" in English. */
export const formatPercent = (basisPoints: number, language: Language): string =>
  new Intl.NumberFormat(LOCALES[language], { style: 'percent', maximumFractionDigits: 2 }).format(
    basisPoints / 10000,
  );

/**
 * A calendar date (`YYYY-MM-DD`, an expiry) or an instant (an ISO timestamp). A bare date is read as local
 * midnight: parsed as UTC it would show the day before anywhere west of Greenwich.
 */
export const formatDate = (value: string | null, language: Language): string => {
  if (!value) {
    return NONE;
  }

  const date = value.length === 10 ? new Date(`${value}T00:00:00`) : new Date(value);

  return new Intl.DateTimeFormat(LOCALES[language], { dateStyle: 'medium' }).format(date);
};

/** A size in bytes, in the largest unit that keeps it above one: "7,2 MB". */
export const formatBytes = (bytes: number, language: Language): string => {
  const units: Array<string> = ['B', 'kB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;

  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }

  return `${new Intl.NumberFormat(LOCALES[language], { maximumFractionDigits: unit === 0 ? 0 : 1 }).format(value)} ${units[unit]}`;
};

export const formatDateTime = (value: string | null, language: Language): string =>
  value
    ? new Intl.DateTimeFormat(LOCALES[language], { dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(value),
      )
    : NONE;
